import React from 'react';
import {
    Shield,
    LineChart,
    Landmark,
    CreditCard,
    FileBarChart,
    Cpu,
} from 'lucide-react';

type TopicGroup = {
    key: string;
    title: string;
    blurb: string;
    icon: React.ReactNode;
    instruments: Array<{ short: string; full: string }>;
};

const ICON_CLASS = 'w-4 h-4 text-fg-tertiary';

const TOPICS: TopicGroup[] = [
    {
        key: 'capital',
        title: 'Capital & prudential',
        blurb: 'Own-funds rules, buffers, leverage, liquidity.',
        icon: <Shield className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'CRR',     full: 'Capital Requirements Regulation' },
            { short: 'CRD',     full: 'Capital Requirements Directive' },
            { short: 'LCR DA',  full: 'Liquidity Coverage Delegated Act' },
            { short: 'IFR',     full: 'Investment Firms Regulation' },
        ],
    },
    {
        key: 'markets',
        title: 'Markets & infrastructure',
        blurb: 'Investor protection, derivatives, market infrastructure.',
        icon: <LineChart className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'MiFID II',        full: 'Markets in Financial Instruments Directive' },
            { short: 'MiFIR',           full: 'Markets in Financial Instruments Regulation' },
            { short: 'EMIR',            full: 'European Market Infrastructure Regulation' },
            { short: 'SFTR',            full: 'Securities Financing Transactions Regulation' },
            { short: 'Securitisation', full: 'Securitisation Regulation' },
        ],
    },
    {
        key: 'resolution',
        title: 'Supervision & resolution',
        blurb: 'SSM, SRM, recovery and resolution, ESAs.',
        icon: <Landmark className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'SSM',  full: 'Single Supervisory Mechanism Regulation' },
            { short: 'SRM',  full: 'Single Resolution Mechanism Regulation' },
            { short: 'BRRD', full: 'Bank Recovery and Resolution Directive' },
            { short: 'EBA',  full: 'EBA founding regulation' },
            { short: 'ESMA', full: 'ESMA founding regulation' },
        ],
    },
    {
        key: 'payments',
        title: 'Payments, e-money & consumer credit',
        blurb: 'Payment services, e-money, consumer lending.',
        icon: <CreditCard className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'PSD2',      full: 'Payment Services Directive 2' },
            { short: 'EMD2',      full: 'E-Money Directive 2' },
            { short: 'MCD',       full: 'Mortgage Credit Directive' },
            { short: 'PAD',       full: 'Payment Accounts Directive' },
        ],
    },
    {
        key: 'reporting',
        title: 'Reporting, disclosures & benchmarking',
        blurb: 'FINREP, COREP, supervisory and public disclosures.',
        icon: <FileBarChart className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'FINREP/COREP', full: 'ITS on supervisory reporting' },
            { short: 'Pillar 3 ITS', full: 'ITS on Pillar 3 disclosures' },
            { short: 'SBP',          full: 'Supervisory benchmarking portfolios' },
        ],
    },
    {
        key: 'digital',
        title: 'Digital, crypto & AML',
        blurb: 'Operational resilience, crypto-assets, financial crime.',
        icon: <Cpu className={ICON_CLASS} strokeWidth={1.5} />,
        instruments: [
            { short: 'DORA',        full: 'Digital Operational Resilience Act' },
            { short: 'MiCA',        full: 'Markets in Crypto-Assets Regulation' },
            { short: 'AMLR',        full: 'Anti-Money Laundering Regulation' },
            { short: 'AMLA',        full: 'AML Authority Regulation' },
            { short: '6AMLD',       full: '6th Anti-Money Laundering Directive' },
            { short: 'AMLD 2024',   full: '2024 Anti-Money Laundering Directive' },
        ],
    },
];

interface CorpusShowcaseProps {
    onChipClick?: (seedText: string) => void;
}

export default function CorpusShowcase({ onChipClick }: CorpusShowcaseProps = {}) {
    const clickable = typeof onChipClick === 'function';
    return (
        <section className="mb-4" aria-labelledby="corpus-heading">
            {/* Section header */}
            <div className="flex items-baseline justify-between mb-2">
                <span
                    id="corpus-heading"
                    className="font-body text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-secondary"
                >
                    What you can ask about
                </span>
            </div>

            {/* Lead */}
            <p className="font-body text-[14px] leading-[1.6] text-fg-secondary max-w-[680px] mb-5">
                BARK covers core EU banking, markets, payments, AML, crypto and
                digital-resilience rules, with key Level 2 instruments.
            </p>

            {/* Topic grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {TOPICS.map((topic) => (
                    <div
                        key={topic.key}
                        className="group flex flex-col p-4 bg-surface border border-border-default rounded-lg hover:border-accent-border hover:bg-accent-tint transition-colors"
                    >
                        {/* Icon + title */}
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="group-hover:text-accent-hover transition-colors">
                                {topic.icon}
                            </span>
                            <h3 className="font-body text-[13px] font-semibold text-fg">
                                {topic.title}
                            </h3>
                        </div>

                        {/* Blurb */}
                        <p className="font-body text-[11px] leading-relaxed text-fg-secondary mb-3">
                            {topic.blurb}
                        </p>

                        {/* Instrument chips — clickable when onChipClick is provided
                            (seeds the composer with "Tell me about X"). */}
                        <div className="flex flex-wrap gap-1.5 mt-auto">
                            {topic.instruments.map((i) => (
                                clickable ? (
                                    <button
                                        key={i.short}
                                        type="button"
                                        title={`${i.full} — click to ask`}
                                        onClick={() => onChipClick!(`Tell me about ${i.short}`)}
                                        className="inline-flex items-center px-2 py-0.5 rounded-sm bg-bg-warm font-mono text-[11px] text-fg tabular-nums hover:bg-accent-tint hover:text-accent-hover transition-colors"
                                    >
                                        {i.short}
                                    </button>
                                ) : (
                                    <span
                                        key={i.short}
                                        title={i.full}
                                        className="inline-flex items-center px-2 py-0.5 rounded-sm bg-bg-warm font-mono text-[11px] text-fg tabular-nums"
                                    >
                                        {i.short}
                                    </span>
                                )
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
